import React, { useState, useEffect, useRef, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "./MapView.css";
import farligeKrydsData from "../data/farlige-kryds.json";

// ── Farlige kryds helpers ─────────────────────────────────────────────────────

function colorFor(ulykker) {
  if (ulykker >= 40) return "#bd1a1a";
  if (ulykker >= 30) return "#f97316";
  if (ulykker >= 20) return "#fbbf24";
  return "#4ade80";
}

function radiusFor(ulykker) {
  if (ulykker >= 40) return 13;
  if (ulykker >= 30) return 11;
  if (ulykker >= 20) return 9;
  return 8;
}

// ── Traffic accidents helpers ─────────────────────────────────────────────────

const myLogoIcon = L.icon({
  iconUrl: "traffic-sign-logo2.png",
  iconSize: [38, 38],
  iconAnchor: [19, 38],
  popupAnchor: [0, -38],
});

function processGerningData(eventsData) {
  const counts = {};
  eventsData.forEach((event) => {
    const gerning = event.Gerningskode.trim();
    counts[gerning] = (counts[gerning] || 0) + 1;
  });
  return Object.entries(counts).map(([gerning, count]) => ({ gerning, count }));
}

function formatTimestampToDDMMYY(timestamp) {
  const dateObj = new Date(timestamp);
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = String(dateObj.getFullYear()).slice(2);
  return `${day}-${month}-${year}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

const MapView = ({ eventsData }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const activeLayerRef = useRef(null);

  const [activeDataset, setActiveDataset] = useState("farlige-kryds");
  const [useClustering, setUseClustering] = useState(true);
  const [selectedGernings, setSelectedGernings] = useState([]);
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  const gerningData = useMemo(() => processGerningData(eventsData), [eventsData]);

  const filteredEvents = useMemo(
    () =>
      selectedGernings.length === 0
        ? eventsData
        : eventsData.filter((e) => selectedGernings.includes(e.Gerningskode)),
    [eventsData, selectedGernings]
  );

  // Initialise map once
  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [55.6761, 12.5683],
      zoom: 12,
      zoomControl: false,
    });

    L.tileLayer("https://tiles.jfmtech.dk/styles/osm-bright/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors, Tiles &copy; JFM",
      maxZoom: 19,
    }).addTo(map);

    L.control.zoom({ position: "bottomright" }).addTo(map);

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      activeLayerRef.current = null;
    };
  }, []);

  // Rebuild markers when dataset / filters / clustering change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (activeLayerRef.current) {
      map.removeLayer(activeLayerRef.current);
      activeLayerRef.current = null;
    }

    if (activeDataset === "farlige-kryds") {
      const layer = L.layerGroup();

      farligeKrydsData.forEach((kryds) => {
        const marker = L.circleMarker([kryds.lat, kryds.lng], {
          radius: radiusFor(kryds.ulykker),
          fillColor: colorFor(kryds.ulykker),
          color: "#fff",
          weight: 2,
          fillOpacity: 0.9,
        });
        marker.bindPopup(
          `<div>
            <strong>${kryds.name}</strong><br/>
            <strong>Ulykker:</strong> ${kryds.ulykker}<br/>
            <strong>Tilskadekomne:</strong> ${kryds.antal}
          </div>`
        );
        marker.on({
          mouseover() { marker.setStyle({ weight: 3, fillOpacity: 1 }); },
          mouseout()  { marker.setStyle({ weight: 2, fillOpacity: 0.9 }); },
        });
        marker.addTo(layer);
      });

      layer.addTo(map);
      activeLayerRef.current = layer;
    } else {
      // Traffic accidents
      const buildMarker = (event) => {
        const [long, lat] = event.coords;
        const marker = L.marker([lat, long], { icon: myLogoIcon });
        marker.bindPopup(
          `<div>
            <strong>Gerningskode:</strong> ${event.Gerningskode}<br/>
            <strong>Adresse:</strong> ${event.adresse_}<br/>
            <strong>Dato:</strong> ${formatTimestampToDDMMYY(event["Anm. dato"])}<br/>
            <strong>Lokalitet:</strong> ${event.Lokalitet}
          </div>`
        );
        return marker;
      };

      if (useClustering) {
        const clusterGroup = L.markerClusterGroup({
          iconCreateFunction(cluster) {
            const count = cluster.getChildCount();
            let color = "yellow";
            if (count >= 50) color = "red";
            else if (count >= 10) color = "orange";
            return L.divIcon({
              html: `<div style="background-color:${color};color:black"><span>${count}</span></div>`,
              className: "custom-cluster-icon",
              iconSize: L.point(40, 40, true),
            });
          },
        });
        filteredEvents.forEach((e) => clusterGroup.addLayer(buildMarker(e)));
        clusterGroup.addTo(map);
        activeLayerRef.current = clusterGroup;
      } else {
        const layer = L.layerGroup(filteredEvents.map(buildMarker));
        layer.addTo(map);
        activeLayerRef.current = layer;
      }
    }
  }, [activeDataset, filteredEvents, useClustering]);

  const handleCheckboxChange = (gerning) => {
    setSelectedGernings((prev) =>
      prev.includes(gerning) ? prev.filter((g) => g !== gerning) : [...prev, gerning]
    );
  };

  const handleSelectAll = () => {
    setSelectedGernings(
      selectedGernings.length === gerningData.length ? [] : gerningData.map((i) => i.gerning)
    );
  };

  // ── Styles ──────────────────────────────────────────────────────────────────

  const btnBase = {
    height: "36px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "13px",
    padding: "0 14px",
    transition: "opacity 0.2s",
  };

  const datasetBtn = (dataset) => ({
    ...btnBase,
    backgroundColor: activeDataset === dataset ? "#bd1a1a" : "#e5e7eb",
    color: activeDataset === dataset ? "white" : "#374151",
  });

  return (
    <div style={{ position: "relative", height: "100vh", width: "100vw" }}>

      {/* Dataset toggle */}
      <div style={{ position: "absolute", top: "10px", left: "10px", zIndex: 1001, display: "flex", gap: "6px" }}>
        <button style={datasetBtn("farlige-kryds")} onClick={() => setActiveDataset("farlige-kryds")}>
          Farlige kryds
        </button>
        <button style={datasetBtn("traffic")} onClick={() => setActiveDataset("traffic")}>
          Trafikulykker
        </button>
      </div>

      {/* Filter toggle — only shown for traffic dataset */}
      {activeDataset === "traffic" && (
        <button
          onClick={() => setIsFilterVisible(!isFilterVisible)}
          style={{
            ...btnBase,
            position: "absolute",
            top: "54px",
            left: "10px",
            zIndex: 1001,
            width: "130px",
            backgroundColor: "#bd1a1a",
            color: "white",
          }}
        >
          {isFilterVisible ? "Skjul filter" : "Vis filter"}
        </button>
      )}

      {/* Filter panel */}
      {activeDataset === "traffic" && isFilterVisible && (
        <div
          style={{
            position: "absolute",
            top: "98px",
            left: "10px",
            zIndex: 1001,
            backgroundColor: "rgba(255,255,255,0.95)",
            padding: "10px",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <span style={{ fontWeight: "bold" }}>Filters</span>
            <div style={{ marginLeft: "16px" }}>
              <label style={{ marginRight: "10px" }}>
                <input type="radio" checked={useClustering} onChange={() => setUseClustering(true)} /> Klynget
              </label>
              <label>
                <input type="radio" checked={!useClustering} onChange={() => setUseClustering(false)} /> Separeret
              </label>
            </div>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "4px" }}>Filter by Gerning:</label>
            <div style={{ maxHeight: "200px", overflowY: "auto", padding: "5px", border: "1px solid #ccc", borderRadius: "5px", backgroundColor: "#f9f9f9" }}>
              <label style={{ display: "block" }}>
                <input type="checkbox" checked={selectedGernings.length === gerningData.length} onChange={handleSelectAll} />
                {" "}Select All
              </label>
              {gerningData.map((item, index) => (
                <div key={`gerning-${index}`}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedGernings.includes(item.gerning)}
                      onChange={() => handleCheckboxChange(item.gerning)}
                    />
                    <span style={{ marginLeft: "5px" }}>{item.gerning} ({item.count})</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Farlige kryds legend */}
      {activeDataset === "farlige-kryds" && (
        <div style={{ position: "absolute", bottom: "30px", right: "10px", zIndex: 1001, backgroundColor: "rgba(255,255,255,0.95)", borderRadius: "6px", boxShadow: "0 2px 10px rgba(0,0,0,0.2)", padding: "10px 14px", fontSize: "12px" }}>
          <div style={{ fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280", marginBottom: "6px" }}>Ulykker</div>
          {[
            { color: "#bd1a1a", label: "40 eller flere" },
            { color: "#f97316", label: "30–39" },
            { color: "#fbbf24", label: "20–29" },
            // { color: "#4ade80", label: "Under 20" },
          ].map(({ color, label }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
              <span style={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: color, border: "2px solid rgba(0,0,0,0.15)", flexShrink: 0 }} />
              <span style={{ color: "#374151" }}>{label}</span>
            </div>
          ))}
        </div>
      )}

      <div ref={mapContainerRef} style={{ height: "100%", width: "100%" }} />
    </div>
  );
};

export default MapView;
